import { useSubscription } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import { ScrollRestoration, useLocation } from 'react-router-dom';
import localLogout, {
  shouldIgnoreLocalLogout,
} from '../../account/localLogout';
import { gql } from '../../graphql/generated/gql';
import useAutoShowInvitationModalEffect from '../../organization/useAutoShowInvitationModalEffect';
import useScript from '../../utils/network/useScript';
import { MainContentHistoryProvider } from '../../utils/router/useMainContentHistory';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import useShortcut from '../../utils/useShortcut';
import { useVersionCheck } from '../checkVersion';
import useAppSelector from '../redux/useAppSelector';
import EnsureAdmin from '../router/EnsureAdmin';
import EnsureLoggedIn from '../router/EnsureLoggedIn';
import EnsureLoggedOut from '../router/EnsureLoggedOut';
import useRouteHandle from '../useRouteHandle';
import PageLayout from './PageLayout';
import SidebarShiftStateProvider from './SidebarShiftStateProvider';

const SESSION_LOGOUT_SUBSCRIPTION = gql(`
  subscription sessionLogout {
    sessionLogout
  }
`);

const ROLE_WRAPPER = {
  user: EnsureLoggedIn,
  admin: EnsureAdmin,
  anonymous: EnsureLoggedOut,
};

const useGoogleAnalytics = () => {
  // We delay the initialization of Google Analytics instead of using a script tag
  // to make sure page title is set correctly before the page view event is sent.
  const id = window.__slab.GOOGLE_ANALYTICS_MEASUREMENT_ID;

  const hasInitializedRef = useRef(false);
  if (!hasInitializedRef.current) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // Google Analytics doesn't work with rest parameters
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', id);

    hasInitializedRef.current = true;
  }

  useScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
};

const AppRoot = () => {
  const isLoggedIn = useAppSelector((state) => !!state.session.user.id);
  const [topicId] = useSlug('topicSlug');

  const navigate = useNavigate();

  useShortcut('shift+n', (e) => {
    e.preventDefault();
    navigate(createPostPath({ topicId: topicId || undefined }));
  });

  useGoogleAnalytics();
  useVersionCheck();
  useAutoShowInvitationModalEffect();

  useSubscription(SESSION_LOGOUT_SUBSCRIPTION, {
    skip: !isLoggedIn,
    onData() {
      if (!shouldIgnoreLocalLogout()) {
        localLogout();
      }
    },
  });

  const location = useLocation();
  const { routeName, pageLayout, ensureRole } = useRouteHandle();

  useEffect(() => {
    if (routeName) {
      analytics.page(routeName);
      window.Intercom?.('update');
    }
  }, [routeName, location]);

  const EnsureWrapper =
    (ensureRole && ROLE_WRAPPER[ensureRole]) || React.Fragment;

  return (
    <MainContentHistoryProvider
      skipLocation={!pageLayout.sidebar || !pageLayout.topbar}
    >
      <ScrollRestoration />
      <EnsureWrapper>
        <SidebarShiftStateProvider>
          <PageLayout elements={pageLayout} routeName={routeName} />
        </SidebarShiftStateProvider>
      </EnsureWrapper>
    </MainContentHistoryProvider>
  );
};

export default () => <AppRoot />;
